import React, { useMemo } from "react";
import {
  DollarSign,
  TrendingDown,
  Clock,
  CheckCircle,
  FileText,
  AlertCircle,
  Calendar,
  CreditCard,
  Activity,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useGetApplicationsByBorrowerQuery } from "../services/loanApplicationApiSlice";
import { useGetLoansByBorrowerQuery } from "../services/loanApiSlice";
import { useGetPaymentsByBorrowerQuery } from "../services/loanPaymentApiSlice";

const Dashboard = () => {
  const { user } = useAuth();
  const borrowerId = user?.borrowerId || user?.id || user?.userId || null;

  const {
    data: applications = [],
    isLoading: appsLoading,
    error: appsError,
  } = useGetApplicationsByBorrowerQuery(borrowerId, { skip: !borrowerId });

  const {
    data: loans = [],
    isLoading: loansLoading,
    error: loansError,
  } = useGetLoansByBorrowerQuery(borrowerId, { skip: !borrowerId });

  const {
    data: payments = [],
    isLoading: paymentsLoading,
  } = useGetPaymentsByBorrowerQuery(borrowerId, { skip: !borrowerId });

  const isLoading = appsLoading || loansLoading || paymentsLoading;

  // ---------- Derived Data (Memoized) ----------
  const {
    activeLoansWithDetails,
    totalOutstanding,
    totalBorrowed,
    averageInterestRate,
    pendingAppsTotal,
    nextPaymentWithDays,
    upcomingPaymentsWithDays,
  } = useMemo(() => {
    // Helper: parse currency safely
    const parseAmount = (val) => parseFloat(val) || 0;

    // 1. Payments grouped by loan (only paid)
    const paidByLoan = payments.reduce((acc, p) => {
      if (p.paymentStatus === "Paid" && p.loanId) {
        acc[p.loanId] = (acc[p.loanId] || 0) + parseAmount(p.amount);
      }
      return acc;
    }, {}); // <-- Fixed: removed TypeScript assertion

    // 2. Active loans with enriched data
    const activeLoans = loans.filter(
      (loan) => loan.status?.toLowerCase() === "active"
    );
    const activeLoansDetailed = activeLoans.map((loan) => {
      const principal = parseAmount(loan.principalAmount || loan.loanAmount);
      const paid = paidByLoan[loan.loanId] || 0;
      const outstanding = Math.max(principal - paid, 0);
      const progress = principal > 0 ? (paid / principal) * 100 : 0;

      // Find next pending payment for this loan
      const nextPayment = payments
        .filter((p) => p.loanId === loan.loanId && p.paymentStatus === "Pending")
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

      return {
        ...loan,
        principal,
        paid,
        outstanding,
        progress: Math.min(progress, 100),
        nextDueDate: nextPayment?.dueDate ? new Date(nextPayment.dueDate) : null,
        nextDueAmount: nextPayment ? parseAmount(nextPayment.amount) : null,
      };
    });

    // 3. Aggregates
    const totalOutstanding = activeLoansDetailed.reduce(
      (sum, loan) => sum + loan.outstanding,
      0
    );

    const totalBorrowed = activeLoansDetailed.reduce(
      (sum, loan) => sum + loan.principal,
      0
    ) || applications
        .filter((app) => app.status?.toLowerCase() === "accepted")
        .reduce((sum, app) => sum + parseAmount(app.requestedAmount), 0);

    const avgInterest = activeLoansDetailed.length
      ? activeLoansDetailed.reduce(
          (sum, loan) => sum + parseAmount(loan.interestRate),
          0
        ) / activeLoansDetailed.length
      : 0;

    const pendingAppsTotal = applications
      .filter((app) => app.status?.toLowerCase() === "pending")
      .reduce((sum, app) => sum + parseAmount(app.requestedAmount), 0);

    // 4. Next overall payment
    const allPendingPayments = payments
      .filter((p) => p.paymentStatus === "Pending")
      .map((p) => ({
        ...p,
        dueDateObj: new Date(p.dueDate),
        amountNum: parseAmount(p.amount),
      }))
      .sort((a, b) => a.dueDateObj.getTime() - b.dueDateObj.getTime());

    const nextPayment = allPendingPayments[0];
    const daysUntilNext = nextPayment
      ? Math.ceil(
          (nextPayment.dueDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
      : null;

    // 5. Upcoming payments with days left
    const upcomingPaymentsWithDays = allPendingPayments.slice(0, 3).map((p) => ({
      ...p,
      daysUntil: Math.ceil(
        (p.dueDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      ),
    }));

    return {
      activeLoansWithDetails: activeLoansDetailed,
      totalOutstanding,
      totalBorrowed,
      averageInterestRate: avgInterest,
      pendingAppsTotal,
      nextPaymentWithDays: nextPayment
        ? { ...nextPayment, daysUntil: daysUntilNext }
        : null,
      upcomingPaymentsWithDays,
    };
  }, [applications, loans, payments]);

  // ---------- Statistics Cards (Enhanced) ----------
  const stats = useMemo(() => {
    const activeLoansCount = activeLoansWithDetails.length;
    const pendingAppsCount = applications.filter(
      (app) => app.status?.toLowerCase() === "pending"
    ).length;

    return [
      {
        label: "Active Loans",
        value: activeLoansCount.toString(),
        subValue:
          activeLoansCount > 0
            ? `$${totalOutstanding.toLocaleString()} outstanding`
            : "No active loans",
        icon: DollarSign,
        gradient: "from-blue-500 to-blue-600",
        lightBg: "bg-blue-50",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
      },
      {
        label: "Total Borrowed",
        value: `Rs${totalBorrowed.toLocaleString(undefined, {
          maximumFractionDigits: 0,
        })}`,
        subValue:
          averageInterestRate > 0
            ? `Avg. ${averageInterestRate.toFixed(1)}% APR`
            : "Includes accepted apps",
        icon: TrendingDown,
        gradient: "from-orange-500 to-orange-600",
        lightBg: "bg-orange-50",
        iconBg: "bg-orange-100",
        iconColor: "text-orange-600",
      },
      {
        label: "Pending Apps",
        value: pendingAppsCount.toString(),
        subValue: `Rs${pendingAppsTotal.toLocaleString()} requested`,
        icon: FileText,
        gradient: "from-purple-500 to-purple-600",
        lightBg: "bg-purple-50",
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
      },
      {
        label: "Next Payment",
        value: nextPaymentWithDays
          ? nextPaymentWithDays.dueDateObj.toLocaleDateString("LKR", {
              month: "short",
              day: "numeric",
            })
          : "—",
        subValue: nextPaymentWithDays
          ? `Rs${nextPaymentWithDays.amountNum.toLocaleString()} • ${
              nextPaymentWithDays.daysUntil > 0
                ? `${nextPaymentWithDays.daysUntil} days`
                : "due today"
            }`
          : "No pending payments",
        icon: Clock,
        gradient: "from-green-500 to-green-600",
        lightBg: "bg-green-50",
        iconBg: "bg-green-100",
        iconColor: "text-green-600",
      },
    ];
  }, [
    activeLoansWithDetails,
    totalOutstanding,
    totalBorrowed,
    averageInterestRate,
    pendingAppsTotal,
    nextPaymentWithDays,
    applications,
  ]);

  // ---------- Recent Approvals ----------
  const recentApprovals = useMemo(() => {
    return applications
      .filter((app) => app.status?.toLowerCase() === "accepted")
      .slice(0, 4)
      .map((app) => ({
        id: app.applicationId,
        amount: `$${parseFloat(app.requestedAmount || 0).toLocaleString()}`,
        date: app.requestedAt
          ? new Date(app.requestedAt).toLocaleDateString()
          : "N/A",
        product: app.loanProductName || "—",
      }));
  }, [applications]);

  // ---------- All Applications (for table) ----------
  const allApplications = useMemo(() => {
    return applications.slice(0, 5).map((app) => ({
      id: app.applicationId,
      amount: `$${parseFloat(app.requestedAmount || 0).toLocaleString()}`,
      date: app.requestedAt
        ? new Date(app.requestedAt).toLocaleDateString()
        : "N/A",
      status: app.status || "Pending",
      product: app.loanProductName || "—",
    }));
  }, [applications]);

  // ---------- Loading & Error States ----------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (appsError || loansError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Something went wrong
          </h2>
          <p className="text-slate-600 mb-6">
            We couldn't load your dashboard. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              Welcome back,{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                {user?.userName || "Borrower"}
              </span>
            </h1>
            <p className="text-slate-500 mt-2 text-lg">
              Here's what's happening with your loans today.
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <div className="bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-slate-200">
              <Calendar className="inline-block w-4 h-4 text-slate-500 mr-2" />
              <span className="text-slate-600 text-sm font-medium">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}
                />
                <div className="relative p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-500">
                        {stat.label}
                      </p>
                      <p className="text-3xl font-bold text-slate-800 mt-1">
                        {stat.value}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-1">
                        {stat.subValue}
                      </p>
                    </div>
                    <div
                      className={`p-3 rounded-xl ${stat.iconBg} group-hover:scale-110 transition-transform`}
                    >
                      <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column (2 cols) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Recent Applications Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-indigo-500" />
                  Recent Loan Applications
                </h2>
                {applications.length > 5 && (
                  <a
                    href="/applications"
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    View all →
                  </a>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allApplications.map((app) => (
                      <tr key={app.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-800">
                          {app.amount}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">{app.date}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{app.product}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              app.status === "Accepted"
                                ? "bg-green-100 text-green-700"
                                : app.status === "Pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : app.status === "Rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {app.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {allApplications.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                          No applications yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Active Loans Table with Progress */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
                  Active Loans
                </h2>
                {activeLoansWithDetails.length > 5 && (
                  <a
                    href="/loans"
                    className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    View all →
                  </a>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Original
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Outstanding
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Next Due
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {activeLoansWithDetails.map((loan) => (
                      <tr key={loan.loanId} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-800">
                          LKR {loan.principalAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          LKR {loan.totalPayable.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {loan.nextDueDate
                            ? loan.nextDueDate.toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: `${loan.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500">
                              {Math.round(loan.progress)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {loan.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {activeLoansWithDetails.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                          No active loans.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Upcoming Payments */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
                <Clock className="w-5 h-5 mr-2 text-amber-500" />
                Upcoming Payments
              </h2>
              {upcomingPaymentsWithDays.length > 0 ? (
                <div className="space-y-4">
                  {upcomingPaymentsWithDays.map((payment) => {
                    const isUrgent = payment.daysUntil <= 7;
                    const isSoon = payment.daysUntil <= 14;
                    return (
                      <div
                        key={payment.paymentId}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-slate-800">
                            ${payment.amountNum.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500">
                            Loan #{payment.loanId.slice(-4)} • Due{" "}
                            {payment.dueDateObj.toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            isUrgent
                              ? "bg-red-100 text-red-700"
                              : isSoon
                              ? "bg-orange-100 text-orange-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {payment.daysUntil > 0 ? `${payment.daysUntil}d` : "Today"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No upcoming payments.</p>
              )}
            </div>

            {/* Recent Approvals */}
            {recentApprovals.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                  Recent Approvals
                </h2>
                <div className="space-y-3">
                  {recentApprovals.map((app) => (
                    <div
                      key={app.id}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-800">{app.amount}</p>
                        <p className="text-xs text-slate-500">{app.product}</p>
                      </div>
                      <span className="text-xs text-slate-400">{app.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Loan Activity Summary */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-sm p-6 text-white">
              <Activity className="w-8 h-8 mb-3 opacity-80" />
              <h3 className="text-lg font-semibold mb-1">Loan Activity</h3>
              <p className="text-sm opacity-90 mb-4">
                You've borrowed{" "}
                <span className="font-bold">
                  Rs{totalBorrowed.toLocaleString()}
                </span>{" "}
                in total, with{" "}
                <span className="font-bold">
                  Rs{totalOutstanding.toLocaleString()}
                </span>{" "}
                outstanding across {activeLoansWithDetails.length} active loan
                {activeLoansWithDetails.length !== 1 ? "s" : ""}.
              </p>
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-white/20 text-white">
                    Repayment Progress
                  </div>
                  <div className="text-xs font-semibold text-white">
                    {totalBorrowed > 0
                      ? `${Math.round(
                          ((totalBorrowed - totalOutstanding) / totalBorrowed) * 100
                        )}%`
                      : "0%"}
                  </div>
                </div>
                <div className="overflow-hidden h-2 text-xs flex rounded-full bg-white/20">
                  <div
                    style={{
                      width: `${
                        totalBorrowed > 0
                          ? ((totalBorrowed - totalOutstanding) / totalBorrowed) * 100
                          : 0
                      }%`,
                    }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-white"
                  />
                </div>
              </div>
              <p className="text-xs mt-3 opacity-75">
                {activeLoansWithDetails.length} of {loans.length} loans active
              </p>
            </div>
          </div>
        </div>

        {/* Platform Features */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center lg:text-left">
            Platform Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform group-hover:bg-indigo-100">
                <CreditCard className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Loan Management</h3>
              <p className="text-sm text-slate-500">
                Create, track, and manage applications seamlessly.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform group-hover:bg-emerald-100">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Quick Approval</h3>
              <p className="text-sm text-slate-500">
                Get instant decisions with automated workflows.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform group-hover:bg-purple-100">
                <DollarSign className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Secure System</h3>
              <p className="text-sm text-slate-500">
                Your financial data is encrypted and protected.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform group-hover:bg-amber-100">
                <TrendingUp className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Reports & Analytics</h3>
              <p className="text-sm text-slate-500">
                Monitor borrowing history and repayment trends.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;